// اقدام‌های چرخه نوبت (SM-Appointment) مشترک برگه روز (SCR-STAFF-301) و جزئیات (SCR-STAFF-303): CheckIn، غیبت، اصلاح غیبت و
// لغو با دلیل (AC-SCH-031.1؛ ReasonDialog الگوی IA §3 با فهرست دلایل لغو مستأجر).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type AppointmentView, type CancelledBy } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits } from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { schKeys, useCancelReasons, useReasonLabel } from './common';

/** Mutationهای چرخه نوبت با بی‌اعتبارسازی همه Queryهای SCH. */
export function useAppointmentMutations() {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: schKeys.all(session?.tenantId) });

  const checkIn = useMutation({
    mutationFn: (id: string): Promise<AppointmentView> =>
      unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:check-in', { params: { path: { id } } })),
    onSettled: invalidate,
  });
  const noShow = useMutation({
    mutationFn: ({ id, overrideLock = false, overrideReason = null }: { id: string; overrideLock?: boolean; overrideReason?: string | null }): Promise<AppointmentView> =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/appointments/{id}:no-show', {
          params: { path: { id } },
          body: { overrideLock, overrideReason },
        }),
      ),
    onSettled: invalidate,
  });
  const correctNoShow = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }): Promise<AppointmentView> =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/appointments/{id}:correct-no-show', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSettled: invalidate,
  });
  const cancel = useMutation({
    mutationFn: ({
      id,
      reasonId,
      cancelledBy,
      note,
      overrideLock = false,
      overrideReason = null,
    }: {
      id: string;
      reasonId: string;
      cancelledBy: CancelledBy;
      note: string | null;
      overrideLock?: boolean;
      overrideReason?: string | null;
    }): Promise<AppointmentView> =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/appointments/{id}:cancel', {
          params: { path: { id } },
          body: { reasonId, cancelledBy, note, overrideLock, overrideReason },
        }),
      ),
    onSettled: invalidate,
  });
  // AP-SCH-02: تأیید حضور (US-SCH-034)، شروع و پایان خدمت (US-SCH-035) و معافیت بیعانه (AC-SCH-023.4).
  const confirm = useMutation({
    mutationFn: (id: string): Promise<AppointmentView> =>
      unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:confirm', { params: { path: { id } } })),
    onSettled: invalidate,
  });
  const start = useMutation({
    mutationFn: (id: string): Promise<AppointmentView> =>
      unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:start', { params: { path: { id } } })),
    onSettled: invalidate,
  });
  const complete = useMutation({
    mutationFn: (id: string): Promise<AppointmentView> =>
      unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:complete', { params: { path: { id } } })),
    onSettled: invalidate,
  });
  const waiveDeposit = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }): Promise<AppointmentView> =>
      unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:waive-deposit', { params: { path: { id } }, body: { reason } })),
    onSettled: invalidate,
  });
  return { checkIn, noShow, correctNoShow, cancel, confirm, start, complete, waiveDeposit };
}

export interface CancelAppointmentDialogProps {
  open: boolean;
  appointmentId: string | null;
  patientName?: string | null;
  cancellationWindowHours?: number;
  /** نوبت قفل است؛ لغو فقط با مجوز و دلیل عبور. */
  locked?: boolean;
  onClose: () => void;
  onCancelled: (view: AppointmentView) => void;
}

/** لغو با دلیل الزامی (AC-SCH-031.1) و طرف لغوکننده (BR-SCH-032: لغو مرکز هرگز دیرهنگام نیست). */
export function CancelAppointmentDialog({
  open,
  appointmentId,
  patientName,
  cancellationWindowHours = 24,
  locked = false,
  onClose,
  onCancelled,
}: CancelAppointmentDialogProps) {
  const { t } = useTranslation('sch');
  const titleId = useId();
  const byId = useId();
  const reasons = useCancelReasons(open);
  const label = useReasonLabel();
  const { cancel } = useAppointmentMutations();
  const [reasonId, setReasonId] = useState('');
  const [cancelledBy, setCancelledBy] = useState<CancelledBy>('Patient');
  const [note, setNote] = useState('');
  const [overrideLock, setOverrideLock] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setReasonId('');
      setCancelledBy('Patient');
      setNote('');
      setOverrideLock(false);
      setOverrideReason('');
      setTouched(false);
      cancel.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    setTouched(true);
    if (!appointmentId || !reasonId || (locked && (!overrideLock || overrideReason.trim().length < 10))) {
      return;
    }
    cancel.mutate(
      {
        id: appointmentId,
        reasonId,
        cancelledBy,
        note: note.trim() || null,
        overrideLock: locked ? overrideLock : false,
        overrideReason: locked ? overrideReason.trim() : null,
      },
      { onSuccess: (view) => onCancelled(view) },
    );
  };

  return (
    <Dialog open={open} onClose={() => !cancel.isPending && onClose()} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>
        {t('sch:cancel.title')}
        {patientName ? ` · ${patientName}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            autoFocus
            required
            label={t('sch:cancel.reason')}
            value={reasonId}
            error={touched && !reasonId}
            helperText={touched && !reasonId ? t('common:validation.required') : undefined}
            onChange={(event) => {
              setReasonId(event.target.value);
              const reason = reasons.data?.find((item) => item.id === event.target.value);
              if (reason) {
                setCancelledBy(reason.defaultCancelledBy ?? 'Patient');
              }
            }}
            inputProps={{ 'data-testid': 'cancel-reason' }}
          >
            {(reasons.data ?? []).map((reason) => (
              <MenuItem key={reason.id} value={reason.id}>
                {label(reason)}
              </MenuItem>
            ))}
          </TextField>
          <FormControl>
            <FormLabel id={byId}>{t('sch:cancel.cancelled_by')}</FormLabel>
            <RadioGroup
              row
              aria-labelledby={byId}
              value={cancelledBy}
              onChange={(event) => setCancelledBy(event.target.value as CancelledBy)}
            >
              <FormControlLabel value="Patient" control={<Radio />} label={t('sch:cancel.by_patient')} />
              <FormControlLabel value="Center" control={<Radio />} label={t('sch:cancel.by_center')} />
            </RadioGroup>
          </FormControl>
          <TextField
            label={t('sch:cancel.note')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={2}
          />
          {locked ? (
            <Stack spacing={1} data-testid="cancel-lock-override">
              <Alert severity="info">{t('sch:cancel.locked_hint')}</Alert>
              <FormControlLabel
                control={<Checkbox checked={overrideLock} onChange={(event) => setOverrideLock(event.target.checked)} />}
                label={t('sch:cancel.override_lock')}
              />
              <TextField
                label={t('sch:booking.override_reason')}
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.target.value)}
                error={touched && overrideReason.trim().length < 10}
                inputProps={{ 'data-testid': 'cancel-override-reason', minLength: 10 }}
              />
            </Stack>
          ) : null}
          <Alert severity="info">{t('sch:cancel.late_hint', { hours: faDigits(cancellationWindowHours) })}</Alert>
          {cancel.error ? <ErrorState error={cancel.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={cancel.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button color="error" variant="contained" onClick={submit} disabled={cancel.isPending} data-testid="cancel-confirm">
          {t('sch:cancel.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
